import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  archiveClientProfile,
  createClientProfile,
  fetchClientAssignees,
  fetchClientProfiles,
  getApiErrorMessage,
  patchMonthCheck,
  updateClientProfile,
} from "../api";
import ErrorRetry from "../components/ErrorRetry";
import Loading from "../components/Loading";
import { useAuth } from "../context/AuthContext";
import { STAFF_ROLES } from "../constants/staffRoles";
import { hasPermission, PERM } from "../permissions";
import { DEFAULT_REGIME, innOwnershipWarning, taxRegimeConflict, taxRegimeWarning } from "./taxRegime";
import { formatShortDate } from "./vacationDates";

function isDesktopPointer() {
  return Boolean(typeof window !== "undefined" && window.matchMedia?.("(pointer: fine)")?.matches);
}

function parseOpenIds(raw) {
  return new Set(
    String(raw || "")
      .split(",")
      .map((value) => Number(value))
      .filter((id) => Number.isFinite(id) && id > 0)
  );
}

function patchParams(prev, patch) {
  const next = new URLSearchParams(prev);
  Object.entries(patch).forEach(([key, value]) => {
    if (value == null || value === "") next.delete(key);
    else next.set(key, String(value));
  });
  return next;
}

const EMPTY_FORM = {
  name: "",
  inn: "",
  ownership_type: "",
  tax_system: "",
  regime_code: "",
  has_employees: "",
  info: "",
  monthly_fee: "",
  payment_day: "",
  salary_day: "",
  advance_day: "",
  primary_employee_ids: [],
  lead_employee_ids: [],
  bank_employee_ids: [],
  uses_kontur: false,
  complexity_index: "",
  reminders: [],
};

const OWNERSHIP_OPTIONS = ["ООО", "ИП", "ЖСК"];
const COMPLEXITY_OPTIONS = Array.from({ length: 10 }, (_, i) => String(i + 1));

const FEE_FORMAT = new Intl.NumberFormat("ru-RU", {
  style: "currency",
  currency: "RUB",
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});
const TAX_OPTIONS = ["УСН (Доходы)", "УСН (Доходы-расходы)", "АУСН", "ОСНО", "ПСН"];
const REGIME_OPTIONS = [
  "УСНд",
  "УСНд-р",
  "УСНд+НДС",
  "УСНд+ПСН",
  "УСНд-р+НДС",
  "УСНд-р+ПСН",
  "УСН+ПСН",
  "УСН 6%",
  "АУСН",
  "АУСНд",
  "ОСНО",
  "ОСНО с марта",
  "ПСН",
];

const Icons = {
  Search: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="11" cy="11" r="8"></circle>
      <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
    </svg>
  ),
  Plus: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="12" y1="5" x2="12" y2="19"></line>
      <line x1="5" y1="12" x2="19" y2="12"></line>
    </svg>
  ),
  Edit: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
      <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
    </svg>
  ),
  Trash: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="3 6 5 6 21 6"></polyline>
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
    </svg>
  ),
  Restore: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="1 4 1 10 7 10"></polyline>
      <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"></path>
    </svg>
  ),
  Empty: () => (
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 21h18"></path>
      <path d="M5 21V7l8-4v18"></path>
      <path d="M19 21V11l-6-4"></path>
    </svg>
  ),
};

function staffIdsByRole(profile, role) {
  const staff = Array.isArray(profile?.staff) ? profile.staff : [];
  return staff.filter((m) => m.role === role).map((m) => m.employee_id);
}

function toFormValues(profile) {
  return {
    name: profile.name || "",
    inn: profile.inn || "",
    ownership_type: profile.ownership_type || "",
    tax_system: profile.tax_system || "",
    regime_code: profile.regime_code || "",
    has_employees:
      profile.has_employees === true ? "yes" : profile.has_employees === false ? "no" : "",
    info: profile.info || "",
    monthly_fee: profile.monthly_fee == null ? "" : String(profile.monthly_fee),
    payment_day: profile.payment_day == null ? "" : String(profile.payment_day),
    salary_day: profile.salary_day == null ? "" : String(profile.salary_day),
    advance_day: profile.advance_day == null ? "" : String(profile.advance_day),
    primary_employee_ids: staffIdsByRole(profile, "primary"),
    lead_employee_ids: staffIdsByRole(profile, "lead"),
    bank_employee_ids: staffIdsByRole(profile, "bank"),
    uses_kontur: Boolean(profile.uses_kontur),
    complexity_index: profile.complexity_index == null ? "" : String(profile.complexity_index),
    reminders: (profile.reminders || []).map((r) => ({
      id: r.id,
      trigger: r.trigger,
      day_of_month: r.day_of_month == null ? "" : String(r.day_of_month),
      text: r.text || "",
      is_note: Boolean(r.is_note),
    })),
  };
}

function parseMonthlyFee(raw) {
  const trimmed = String(raw ?? "").trim().replace(/\s/g, "").replace(",", ".");
  if (!trimmed) return { ok: true, value: null };
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return { ok: false };
  const num = Number(trimmed);
  if (!Number.isFinite(num) || num < 0) return { ok: false };
  return { ok: true, value: num };
}

function parsePaymentDay(raw) {
  const trimmed = String(raw ?? "").trim();
  if (!trimmed) return { ok: true, value: null };
  if (!/^\d{1,2}$/.test(trimmed)) return { ok: false };
  const num = Number(trimmed);
  if (!Number.isInteger(num) || num < 1 || num > 31) return { ok: false };
  return { ok: true, value: num };
}

function toPayload(form, { includeFee = false } = {}) {
  const payload = {
    name: form.name.trim(),
    inn: form.inn.trim() || null,
    ownership_type: form.ownership_type.trim() || null,
    tax_system: form.tax_system.trim() || null,
    regime_code: form.regime_code.trim() || null,
    has_employees: form.has_employees === "yes" ? true : form.has_employees === "no" ? false : null,
    info: form.info.trim() || null,
    primary_employee_ids: form.primary_employee_ids || [],
    lead_employee_ids: form.lead_employee_ids || [],
    bank_employee_ids: form.bank_employee_ids || [],
    uses_kontur: Boolean(form.uses_kontur),
    complexity_index: form.complexity_index === "" ? null : Number(form.complexity_index),
  };
  if (includeFee) {
    const fee = parseMonthlyFee(form.monthly_fee);
    payload.monthly_fee = fee.ok ? fee.value : null;
    const day = parsePaymentDay(form.payment_day);
    payload.payment_day = day.ok ? day.value : null;
  }
  if (payload.has_employees === true) {
    const salary = parsePaymentDay(form.salary_day);
    const advance = parsePaymentDay(form.advance_day);
    payload.salary_day = salary.ok ? salary.value : null;
    payload.advance_day = advance.ok ? advance.value : null;
  }
  payload.reminders = (form.reminders || []).map((r) => ({
    ...(Number.isInteger(r.id) ? { id: r.id } : {}),
    trigger: r.trigger,
    day_of_month: r.trigger === "monthly" ? parsePaymentDay(r.day_of_month).value ?? null : null,
    text: r.text.trim(),
    is_note: Boolean(r.is_note),
  }));
  return payload;
}

function reminderErrors(reminders) {
  return (reminders || []).map((r) => {
    if (r.trigger === "monthly") {
      const parsed = parsePaymentDay(r.day_of_month);
      if (!parsed.ok || parsed.value == null) return "День от 1 до 31";
    }
    if (!String(r.text || "").trim()) return "Введите текст";
    return "";
  });
}

function linkedSurveyReason(profile) {
  if (profile.can_delete !== false) return "";
  const parts = [];
  if (profile.link_responses) parts.push(`${profile.link_responses} ответов`);
  if (profile.link_employees) parts.push(`${profile.link_employees} привязок сотрудников`);
  if (profile.link_manager) parts.push(`${profile.link_manager} в отчётах руководителя`);
  if (!parts.length) return "Есть связанные опросы или отчёты";
  return `Участвовал в опросах: ${parts.join(", ")}`;
}

function ClientForm({ form, setForm, disabled, assignees, innError, innRef, nameRef, feeError, feeRef, dayError, dayRef, salaryDayError, advanceDayError, reminderErrors: reminderRowErrors = [], showFee }) {
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });
  const setTax = (e) => {
    const tax_system = e.target.value;
    const next = { ...form, tax_system };
    if (!form.regime_code && DEFAULT_REGIME[tax_system]) {
      next.regime_code = DEFAULT_REGIME[tax_system];
    }
    setForm(next);
  };
  const regimeWarning = taxRegimeWarning(form.tax_system, form.regime_code);
  const innOwnWarning = innOwnershipWarning(form.inn, form.ownership_type);
  const ownershipOptions = OWNERSHIP_OPTIONS.includes(form.ownership_type) || !form.ownership_type
    ? OWNERSHIP_OPTIONS
    : [...OWNERSHIP_OPTIONS, form.ownership_type];
  const taxOptions = TAX_OPTIONS.includes(form.tax_system) || !form.tax_system
    ? TAX_OPTIONS
    : [...TAX_OPTIONS, form.tax_system];
  const regimeOptions = REGIME_OPTIONS.includes(form.regime_code) || !form.regime_code
    ? REGIME_OPTIONS
    : [...REGIME_OPTIONS, form.regime_code];

  const toggleAssignee = (field, employeeId) => {
    const current = form[field] || [];
    const next = current.includes(employeeId)
      ? current.filter((id) => id !== employeeId)
      : [...current, employeeId];
    setForm({ ...form, [field]: next });
  };

  const renderAssigneePicker = (field, label, roleCode) => (
    <fieldset key={roleCode} className="cd-staff-fieldset" disabled={disabled}>
      <legend className="cd-staff-legend">{label}</legend>
      {assignees.length === 0 ? (
        <p className="cd-staff-empty">Нет сотрудников для назначения</p>
      ) : (
        <div className="cd-staff-options">
          {assignees.map((emp) => {
            const checked = (form[field] || []).includes(emp.id);
            return (
              <label key={`${field}-${emp.id}`} className="cd-staff-option">
                <input
                  type="checkbox"
                  name={field}
                  checked={checked}
                  onChange={() => toggleAssignee(field, emp.id)}
                  disabled={disabled}
                />
                <span>{emp.full_name}</span>
              </label>
            );
          })}
        </div>
      )}
    </fieldset>
  );

  return (
    <>
      <label className="login-label client-form-info">
        Название клиента
        <input
          ref={nameRef}
          className="login-input"
          name="name"
          autoComplete="organization"
          value={form.name}
          onChange={set("name")}
          required
          maxLength={300}
          disabled={disabled}
          autoFocus={isDesktopPointer()}
        />
      </label>
      <label className="login-label">
        ИНН
        <input
          ref={innRef}
          className="login-input"
          name="inn"
          autoComplete="off"
          spellCheck={false}
          value={form.inn}
          onChange={set("inn")}
          inputMode="numeric"
          pattern="(\d{10}|\d{12})?"
          title="10 или 12 цифр"
          maxLength={12}
          disabled={disabled}
          aria-invalid={Boolean(innError)}
          aria-describedby={innError ? "client-inn-error" : undefined}
          placeholder="10 или 12 цифр…"
        />
        {innError && (
          <span className="cd-field-error" id="client-inn-error" role="status" aria-live="polite">{innError}</span>
        )}
        {!innError && innOwnWarning && (
          <span className="cd-field-error" role="status">{innOwnWarning}</span>
        )}
      </label>
      <label className="login-label">
        Вид собственности
        <select
          className="login-input"
          name="ownership_type"
          autoComplete="off"
          value={form.ownership_type}
          onChange={set("ownership_type")}
          disabled={disabled}
        >
          <option value="">Не указано</option>
          {ownershipOptions.map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      </label>
      <label className="login-label">
        Система налогообложения
        <select
          className="login-input"
          name="tax_system"
          autoComplete="off"
          value={form.tax_system}
          onChange={setTax}
          disabled={disabled}
        >
          <option value="">Не указано</option>
          {taxOptions.map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      </label>
      <label className="login-label">
        Код режима (отчётность)
        <select
          className="login-input"
          name="regime_code"
          autoComplete="off"
          value={form.regime_code}
          onChange={set("regime_code")}
          disabled={disabled}
        >
          <option value="">Не указано</option>
          {regimeOptions.map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
        {regimeWarning && (
          <span className="cd-field-error" role="status">{regimeWarning}</span>
        )}
      </label>
      <label className="login-label">
        Есть сотрудники?
        <select
          className="login-input"
          name="has_employees"
          autoComplete="off"
          value={form.has_employees}
          onChange={set("has_employees")}
          disabled={disabled}
        >
          <option value="">Не указано</option>
          <option value="yes">Да</option>
          <option value="no">Нет</option>
        </select>
      </label>
      <label className="login-label">
        Индекс сложности
        <select
          className="login-input"
          name="complexity_index"
          autoComplete="off"
          value={form.complexity_index}
          onChange={set("complexity_index")}
          disabled={disabled}
        >
          <option value="">Не указано</option>
          {COMPLEXITY_OPTIONS.map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      </label>
      {form.has_employees === "yes" && (
        <div className="cd-form-pair">
          <label className="login-label">
            День ЗП
            <input
              className="login-input"
              name="salary_day"
              autoComplete="off"
              inputMode="numeric"
              value={form.salary_day}
              onChange={set("salary_day")}
              disabled={disabled}
              placeholder="например, 5…"
              aria-invalid={Boolean(salaryDayError)}
              aria-describedby={salaryDayError ? "client-salary-day-error" : undefined}
            />
            {salaryDayError && (
              <span className="cd-field-error" id="client-salary-day-error" role="status" aria-live="polite">{salaryDayError}</span>
            )}
          </label>
          <label className="login-label">
            День аванса
            <input
              className="login-input"
              name="advance_day"
              autoComplete="off"
              inputMode="numeric"
              value={form.advance_day}
              onChange={set("advance_day")}
              disabled={disabled}
              placeholder="например, 20…"
              aria-invalid={Boolean(advanceDayError)}
              aria-describedby={advanceDayError ? "client-advance-day-error" : undefined}
            />
            {advanceDayError && (
              <span className="cd-field-error" id="client-advance-day-error" role="status" aria-live="polite">{advanceDayError}</span>
            )}
          </label>
        </div>
      )}
      {STAFF_ROLES.map((r) => renderAssigneePicker(r.field, r.label, r.code))}
      <label className="login-label cd-checkbox-label">
        <input
          type="checkbox"
          name="uses_kontur"
          checked={Boolean(form.uses_kontur)}
          onChange={(e) => setForm({ ...form, uses_kontur: e.target.checked })}
          disabled={disabled}
        />
        Кабинет <span translate="no">Контура</span>
      </label>
      <fieldset className="cd-reminders" disabled={disabled}>
        <legend>Напоминания бота</legend>
        {(form.reminders || []).map((r, i) => (
          <div className="cd-reminder-row" key={r.id ?? `new-${i}`}>
            <select
              className="login-input"
              aria-label="Тип напоминания"
              value={r.trigger}
              disabled={disabled}
              onChange={(e) => {
                const trigger = e.target.value;
                const next = (form.reminders || []).map((row, idx) => {
                  if (idx !== i) return row;
                  return {
                    ...row,
                    trigger,
                    day_of_month: trigger !== "monthly" ? "" : row.day_of_month,
                  };
                });
                setForm({ ...form, reminders: next });
              }}
            >
              <option value="monthly">Число месяца</option>
              <option value="after_payroll">После выплаты ЗП/аванса</option>
              <option value="after_advance">После проверки аванса</option>
            </select>
            <button
              type="button"
              aria-label="Удалить напоминание"
              className="cd-reminder-remove"
              disabled={disabled}
              onClick={() => {
                setForm({
                  ...form,
                  reminders: (form.reminders || []).filter((_, idx) => idx !== i),
                });
              }}
            >
              ✕
            </button>
            {r.trigger === "monthly" && (
              <input
                className="login-input"
                aria-label="День"
                inputMode="numeric"
                value={r.day_of_month}
                disabled={disabled}
                onChange={(e) => {
                  const next = (form.reminders || []).map((row, idx) => (
                    idx === i ? { ...row, day_of_month: e.target.value } : row
                  ));
                  setForm({ ...form, reminders: next });
                }}
              />
            )}
            <textarea
              className="login-input"
              aria-label="Текст напоминания"
              value={r.text}
              maxLength={500}
              rows={3}
              disabled={disabled}
              onChange={(e) => {
                const next = (form.reminders || []).map((row, idx) => (
                  idx === i ? { ...row, text: e.target.value } : row
                ));
                setForm({ ...form, reminders: next });
              }}
            />
            <label className="login-label cd-checkbox-label">
              <input
                type="checkbox"
                checked={Boolean(r.is_note)}
                disabled={disabled}
                onChange={(e) => {
                  const next = (form.reminders || []).map((row, idx) => (
                    idx === i ? { ...row, is_note: e.target.checked } : row
                  ));
                  setForm({ ...form, reminders: next });
                }}
              />
              Без кнопки «Сделано»
            </label>
            {reminderRowErrors[i] && (
              <span className="cd-field-error" role="status">{reminderRowErrors[i]}</span>
            )}
          </div>
        ))}
        <button
          type="button"
          className="cd-reminder-add"
          disabled={disabled}
          onClick={() => {
            setForm({
              ...form,
              reminders: [...(form.reminders || []), { trigger: "monthly", day_of_month: "", text: "", is_note: false }],
            });
          }}
        >
          + Напоминание
        </button>
        <p className="cd-reminder-hint">
          Число месяца в выходной сдвигается на последний рабочий день. „После выплаты“ выходит на следующий рабочий день после поста зарплаты или аванса. „После проверки аванса“ — только если в том посте был аванс.
        </p>
      </fieldset>
      {showFee && (
        <div className="cd-form-pair">
          <label className="login-label">
            Оплата,{"\u00A0"}₽/мес
            <input
              ref={feeRef}
              className="login-input"
              name="monthly_fee"
              autoComplete="off"
              inputMode="decimal"
              value={form.monthly_fee}
              onChange={set("monthly_fee")}
              disabled={disabled}
              placeholder="например, 12000…"
              aria-invalid={Boolean(feeError)}
              aria-describedby={feeError ? "client-fee-error" : undefined}
            />
            {feeError && (
              <span className="cd-field-error" id="client-fee-error" role="status" aria-live="polite">{feeError}</span>
            )}
          </label>
          <label className="login-label">
            День оплаты
            <input
              ref={dayRef}
              className="login-input"
              name="payment_day"
              autoComplete="off"
              inputMode="numeric"
              value={form.payment_day}
              onChange={set("payment_day")}
              disabled={disabled}
              placeholder="например, 15…"
              aria-invalid={Boolean(dayError)}
              aria-describedby={dayError ? "client-day-error" : undefined}
            />
            {dayError && (
              <span className="cd-field-error" id="client-day-error" role="status" aria-live="polite">{dayError}</span>
            )}
          </label>
        </div>
      )}
      <label className="login-label client-form-info">
        Информация
        <textarea
          className="login-input"
          name="info"
          autoComplete="off"
          rows={5}
          value={form.info}
          onChange={set("info")}
          disabled={disabled}
          placeholder="Договор, контакты, особенности…"
        />
      </label>
    </>
  );
}

function buildMetaParts(profile, { showFee = false } = {}) {
  const parts = [];
  if (profile.inn) parts.push(`ИНН ${profile.inn}`);
  if (profile.ownership_type) parts.push(profile.ownership_type);
  if (profile.tax_system) parts.push(profile.tax_system);
  if (profile.regime_code) parts.push(`СНО ${profile.regime_code}`);
  if (profile.has_employees === true) parts.push("С сотрудниками");
  if (profile.has_employees === false) parts.push("Без сотрудников");
  if (showFee && profile.monthly_fee != null) {
    parts.push(`${FEE_FORMAT.format(Number(profile.monthly_fee))}/мес`);
  }
  if (showFee && profile.payment_day != null) {
    parts.push(`день оплаты ${profile.payment_day}`);
  }
  return parts;
}

function staffNamesForRole(profile, role) {
  const staff = Array.isArray(profile?.staff) ? profile.staff : [];
  return staff
    .filter((m) => m.role === role)
    .map((m) => m.full_name)
    .filter(Boolean);
}

function substituteLines(profile, role) {
  const members = (profile?.staff || []).filter((m) => m.role === role);
  return members
    .filter((m) => m.substitute)
    .map((m) => {
      const tail = `${m.substitute.full_name} до ${formatShortDate(m.substitute.end_date)}`;
      return members.length > 1 ? `${m.full_name} → ${tail}` : `→ замена: ${tail}`;
    });
}

function StaffAssignmentSummary({ profile }) {
  const bothRoleIds = new Set(
    (profile.staff || [])
      .filter((m) => m.role === "primary")
      .map((m) => m.employee_id)
      .filter((id) =>
        (profile.staff || []).some((m) => m.role === "lead" && m.employee_id === id)
      )
  );

  return (
    <div className="cd-staff-summary">
      {STAFF_ROLES.map((role) => {
        const names = staffNamesForRole(profile, role.code);
        const subs = substituteLines(profile, role.code);
        const required = role.code !== "bank";
        if (!required && names.length === 0 && subs.length === 0) {
          return null;
        }
        const leadShowsBoth =
          role.code === "lead" &&
          names.length > 0 &&
          (profile.staff || []).some((m) => m.role === "lead" && bothRoleIds.has(m.employee_id));
        return (
          <React.Fragment key={role.code}>
            <div className="cd-staff-line">
              <span className="cd-staff-role">{role.label}</span>
              {names.length > 0 ? (
                <span className="cd-staff-names">
                  {names.join(", ")}
                  {leadShowsBoth && <span className="cd-staff-both"> (обе роли)</span>}
                </span>
              ) : required ? (
                <span className="cd-staff-missing">не назначен</span>
              ) : null}
            </div>
            {subs.map((line) => (
              <div key={line} className="cd-staff-substitute">{line}</div>
            ))}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function formatMonthCheckDate(iso) {
  const value = String(iso || "");
  return `${value.slice(8, 10)}.${value.slice(5, 7)}.${value.slice(0, 4)}`;
}

function monthCheckBusyKey(profileId, code) {
  return `${profileId}:${code}`;
}

function monthCheckButtonClass(pressed) {
  return pressed ? "cd-sort-btn cd-metric--active" : "cd-sort-btn";
}

function recomputeMonthCheck(current, items) {
  const filled_count = items.filter((item) => item.state === "done" || item.state === "na").length;
  const closed = items.every((item) => item.state !== "open");
  return {
    ...current,
    items,
    filled_count,
    closed,
    total: current.total,
  };
}

function withMonthCheckItemState(current, code, state) {
  if (!current) return current;
  const items = (current.items || []).map((item) => (
    item.code === code ? { ...item, state } : item
  ));
  return recomputeMonthCheck(current, items);
}

function applyReturnedMonthCheckItem(current, returned, code) {
  const returnedItem = (returned?.items || []).find((item) => item.code === code);
  if (!returnedItem || !current) return current;
  const items = current.items || [];
  const hasItem = items.some((item) => item.code === code);
  const nextItems = hasItem
    ? items.map((item) => (item.code === code ? returnedItem : item))
    : items.concat(returnedItem);
  return recomputeMonthCheck(current, nextItems);
}

function MonthCheckBlock({ check, profileId, busyKeys, onPatch }) {
  const due = formatMonthCheckDate(check.due_on);
  return (
    <div>
      <div>Контроль через месяц</div>
      <div>{check.closed ? "Закрыт" : `Напоминание с ${due}`}</div>
      {(check.items || []).map((item) => {
        const busy = busyKeys.has(monthCheckBusyKey(profileId, item.code));
        const donePressed = item.state === "done";
        const naPressed = item.state === "na";
        return (
          <div key={item.code}>
            <div>{item.title}</div>
            <div>{item.text}</div>
            <button
              type="button"
              className={monthCheckButtonClass(donePressed)}
              aria-label={`${item.title}: сделано`}
              aria-pressed={donePressed}
              disabled={busy}
              onClick={() => onPatch(item.code, donePressed ? "open" : "done")}
            >
              Сделано
            </button>
            <button
              type="button"
              className={monthCheckButtonClass(naPressed)}
              aria-label={`${item.title}: не применимо`}
              aria-pressed={naPressed}
              disabled={busy}
              onClick={() => onPatch(item.code, naPressed ? "open" : "na")}
            >
              Не применимо
            </button>
          </div>
        );
      })}
    </div>
  );
}

const CHIP_TEXT_LIMIT = 40;

function reminderChipFull(reminder) {
  const text = reminder.text ?? "";
  if (reminder.trigger === "monthly") return `${reminder.day_of_month} \u00b7 ${text}`;
  if (reminder.trigger === "after_payroll") return `после выплаты \u00b7 ${text}`;
  if (reminder.trigger === "after_advance") return `после аванса \u00b7 ${text}`;
  return null;
}

function profileReminderChips(profile) {
  const chips = [];
  if (profile.salary_day != null) {
    chips.push({ key: "salary", full: `ЗП ${profile.salary_day}` });
  }
  if (profile.advance_day != null) {
    chips.push({ key: "advance", full: `Аванс ${profile.advance_day}` });
  }
  const reminders = Array.isArray(profile.reminders) ? profile.reminders : [];
  [...reminders]
    .sort((a, b) => Number(a.id) - Number(b.id))
    .forEach((reminder) => {
      const full = reminderChipFull(reminder);
      if (full == null) return;
      chips.push({ key: `reminder-${reminder.id}`, full });
    });
  return chips;
}

function visibleChipText(full) {
  if (full.length <= CHIP_TEXT_LIMIT) return full;
  return `${full.slice(0, CHIP_TEXT_LIMIT - 1)}\u2026`;
}

function ClientRow({
  profile,
  expanded,
  onToggle,
  onEdit,
  onArchive,
  onRestore,
  onToggleSurvey,
  onToggleKontur,
  surveyBusy,
  restoreBusy,
  showFee,
  onMonthCheck,
  monthCheckBusy,
}) {
  const hasLinks = profile.can_delete === false;
  const blocked = linkedSurveyReason(profile);
  const metaParts = buildMetaParts(profile, { showFee });
  const infoText = (profile.info || "").trim();
  const inSurvey = !profile.is_survey_excluded;
  const isArchived = profile.is_active === false;
  const incomplete = profile.assignment_complete === false;
  const snoWarning = taxRegimeWarning(profile.tax_system, profile.regime_code);
  const snoEmpty = Boolean(profile.tax_system && !profile.regime_code);
  const snoClash = taxRegimeConflict(profile.tax_system, profile.regime_code);
  const reminderChips = profileReminderChips(profile);

  return (
    <div
      className={`cd-row ${expanded ? "cd-row--expanded" : ""} ${
        incomplete ? "cd-row--incomplete-staff" : ""
      }`}
    >
      <div className="cd-row-main">
        <button
          type="button"
          className="cd-row-toggle"
          aria-expanded={expanded}
          aria-label={`${expanded ? "Свернуть" : "Раскрыть"} ${profile.name}`}
          onClick={() => onToggle(profile.id)}
        >
          <span className="cd-row-chevron" aria-hidden="true">{expanded ? "▾" : "▸"}</span>
          <div className="cd-row-body">
            <div className="cd-row-top">
              <span className="cd-row-name">{profile.name}</span>
              {isArchived && (
                <span className="cd-badge cd-badge--locked" title="Клиент в архиве">Архив</span>
              )}
              {incomplete && (
                <span className="cd-badge cd-badge--warn">Нет назначения</span>
              )}
              {profile.uses_kontur && (
                <span className="cd-badge cd-badge--accent" title="Кабинет Контура" translate="no">Контур</span>
              )}
              {profile.complexity_index != null && (
                <span className="cd-badge" title="Индекс сложности клиента">Сложность {profile.complexity_index}</span>
              )}
              {hasLinks && (
                <span className="cd-badge cd-badge--locked" title={blocked}>В опросах</span>
              )}
              {snoEmpty && (
                <span className="cd-badge cd-badge--warn" title={snoWarning}>Нет кода режима</span>
              )}
              {snoClash && (
                <span className="cd-badge cd-badge--warn" title={snoWarning}>СНО не сходится</span>
              )}
              {profile.month_check && !profile.month_check.closed && (
                <span
                  className={
                    profile.month_check.reminders_started
                      ? "cd-badge cd-badge--warn"
                      : "cd-badge"
                  }
                  title={
                    profile.month_check.reminders_started
                      ? `Срок наступил ${formatMonthCheckDate(profile.month_check.due_on)}`
                      : `Напоминание с ${formatMonthCheckDate(profile.month_check.due_on)}`
                  }
                >
                  контроль {profile.month_check.filled_count}/{profile.month_check.total}
                </span>
              )}
            </div>
            {reminderChips.length > 0 && (
              <div className="cd-reminder-chips">
                {reminderChips.map((chip) => (
                  <span key={chip.key} className="cd-reminder-chip" title={chip.full}>
                    {visibleChipText(chip.full)}
                  </span>
                ))}
              </div>
            )}
            <div className="cd-row-secondary">
              {metaParts.length > 0 && (
                <div className="cd-row-meta">{metaParts.join(" · ")}</div>
              )}
              <StaffAssignmentSummary profile={profile} />
            </div>
          </div>
        </button>
        <div className="cd-row-actions">
          {!isArchived && (
            <>
            <div className="cd-survey-toggle">
              <span className="cd-survey-toggle-label">В опросе</span>
              <button
                type="button"
                className={`staff-switch ${inSurvey ? "staff-switch--on" : ""}`}
                aria-pressed={inSurvey}
                aria-label={
                  inSurvey
                    ? `${profile.name}: участвует в опросе`
                    : `${profile.name}: исключён из опроса`
                }
                disabled={surveyBusy}
                onClick={() => onToggleSurvey(profile)}
              >
                <span className="staff-switch-track" aria-hidden="true" />
                <span className="staff-switch-label">
                  {inSurvey ? "Да" : "Нет"}
                </span>
              </button>
            </div>
            <div className="cd-survey-toggle">
              <span className="cd-survey-toggle-label" translate="no">Контур</span>
              <button
                type="button"
                className={`staff-switch ${profile.uses_kontur ? "staff-switch--on" : ""}`}
                aria-pressed={Boolean(profile.uses_kontur)}
                aria-label={
                  profile.uses_kontur
                    ? `${profile.name}: кабинет Контура включён`
                    : `${profile.name}: кабинет Контура выключен`
                }
                disabled={surveyBusy}
                onClick={() => onToggleKontur(profile)}
              >
                <span className="staff-switch-track" aria-hidden="true" />
                <span className="staff-switch-label">
                  {profile.uses_kontur ? "Да" : "Нет"}
                </span>
              </button>
            </div>
            </>
          )}
          {showFee && (
            <Link
              className="cd-icon-btn"
              to={`/client-payments?q=${encodeURIComponent(profile.name)}`}
              title="Оплаты клиента"
              aria-label={`Оплаты ${profile.name}`}
              onClick={(e) => e.stopPropagation()}
            >
              ₽
            </Link>
          )}
          <button
            type="button"
            className="cd-icon-btn"
            title="Редактировать"
            aria-label={`Редактировать ${profile.name}`}
            onClick={() => onEdit(profile)}
          >
            <Icons.Edit />
          </button>
          {isArchived ? (
            <button
              type="button"
              className="cd-icon-btn"
              title="Восстановить из архива"
              aria-label={`Восстановить ${profile.name}`}
              disabled={restoreBusy}
              onClick={() => onRestore(profile)}
            >
              <Icons.Restore />
            </button>
          ) : (
            <button
              type="button"
              className="cd-icon-btn cd-danger"
              title="В архив"
              aria-label={`В архив ${profile.name}`}
              onClick={() => onArchive(profile)}
            >
              <Icons.Trash />
            </button>
          )}
        </div>
      </div>
      {expanded && (
        <div className="cd-row-expand">
          {infoText || "Нет дополнительной информации"}
          {reminderChips.length > 0 && (
            <div className="cd-reminder-expand">
              <h3 className="cd-reminder-expand-title">Напоминания</h3>
              <ul className="cd-reminder-expand-list">
                {reminderChips.map((chip) => (
                  <li key={chip.key}>{chip.full}</li>
                ))}
              </ul>
            </div>
          )}
          {profile.month_check && (
            <MonthCheckBlock
              check={profile.month_check}
              profileId={profile.id}
              busyKeys={monthCheckBusy}
              onPatch={(code, state) => onMonthCheck(profile, code, state)}
            />
          )}
        </div>
      )}
    </div>
  );
}

export default function ClientDirectory() {
  const { user } = useAuth();
  const canEditFee = hasPermission(user, PERM.CLIENTS_FEE);
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") || "";
  const filterOwnership = searchParams.get("ownership") || "";
  const filterTax = searchParams.get("tax") || "";
  const filterEmployees = searchParams.get("employees") || "";
  const filterSurvey = searchParams.get("survey") || "";
  const statusFilter = searchParams.get("status") || "active";
  const sortDir = searchParams.get("sort") === "desc" ? "desc" : "asc";
  const expanded = useMemo(() => parseOpenIds(searchParams.get("open")), [searchParams]);
  const cardId = Number(searchParams.get("card") || "");
  const requestedCardId = Number.isFinite(cardId) && cardId > 0 ? cardId : null;
  const updateParams = useCallback((patch) => {
    setSearchParams((prev) => patchParams(prev, patch), { replace: true });
  }, [setSearchParams]);
  const cardLookupRef = useRef(0);

  const [profiles, setProfiles] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionError, setActionError] = useState("");
  const [innError, setInnError] = useState("");
  const [feeError, setFeeError] = useState("");
  const [dayError, setDayError] = useState("");
  const [salaryDayError, setSalaryDayError] = useState("");
  const [advanceDayError, setAdvanceDayError] = useState("");
  const [reminderFieldErrors, setReminderFieldErrors] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [monthCheckBusy, setMonthCheckBusy] = useState(() => new Set());

  const [modalMode, setModalMode] = useState(null); // 'create' | 'edit'
  const [modalTarget, setModalTarget] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [initialForm, setInitialForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [archiving, setArchiving] = useState(false);
  const innRef = useRef(null);
  const feeRef = useRef(null);
  const dayRef = useRef(null);
  const nameRef = useRef(null);
  const formDirty = Boolean(modalMode) && JSON.stringify(form) !== JSON.stringify(initialForm);

  const loadProfiles = useCallback(async ({ soft = false } = {}) => {
    if (!soft) setLoading(true);
    setError("");
    try {
      const [profileData, assigneeData] = await Promise.all([
        fetchClientProfiles(statusFilter),
        fetchClientAssignees().catch(() => []),
      ]);
      setProfiles(profileData);
      setAssignees(assigneeData);
    } catch (err) {
      setError(getApiErrorMessage(err, "Не удалось загрузить клиентов"));
    } finally {
      if (!soft) setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    loadProfiles();
  }, [loadProfiles]);

  useEffect(() => {
    if (!modalMode && !archiveTarget) return undefined;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (saving || archiving) return;
      if (formDirty && !window.confirm("Закрыть без сохранения изменений?")) return;
      setModalMode(null);
      setModalTarget(null);
      setArchiveTarget(null);
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [modalMode, archiveTarget, saving, archiving, formDirty]);

  useEffect(() => {
    if (!formDirty) return undefined;
    const onBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [formDirty]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = profiles.filter((p) => {
      if (filterOwnership && p.ownership_type !== filterOwnership) return false;
      if (filterTax && p.tax_system !== filterTax) return false;
      if (filterEmployees === "yes" && p.has_employees !== true) return false;
      if (filterEmployees === "no" && p.has_employees !== false) return false;
      if (filterEmployees === "unknown" && p.has_employees !== null) return false;
      if (filterSurvey === "in" && p.is_survey_excluded) return false;
      if (filterSurvey === "out" && !p.is_survey_excluded) return false;
      if (!q) return true;
      return (
        p.name.toLowerCase().includes(q) ||
        (p.inn && p.inn.toLowerCase().includes(q)) ||
        (p.tax_system && p.tax_system.toLowerCase().includes(q)) ||
        (p.regime_code && p.regime_code.toLowerCase().includes(q)) ||
        (p.ownership_type && p.ownership_type.toLowerCase().includes(q)) ||
        (p.info && p.info.toLowerCase().includes(q))
      );
    });
    const dir = sortDir === "asc" ? 1 : -1;
    return [...list].sort(
      (a, b) => dir * a.name.localeCompare(b.name, "ru", { sensitivity: "base" })
    );
  }, [profiles, query, filterOwnership, filterTax, filterEmployees, filterSurvey, sortDir]);

  const stats = useMemo(() => {
    let withEmployees = 0;
    let withoutEmployees = 0;
    let unknownEmployees = 0;
    let inSurvey = 0;
    for (const p of profiles) {
      if (p.has_employees === true) withEmployees += 1;
      else if (p.has_employees === false) withoutEmployees += 1;
      else unknownEmployees += 1;
      if (!p.is_survey_excluded) inSurvey += 1;
    }
    return { withEmployees, withoutEmployees, unknownEmployees, inSurvey };
  }, [profiles]);

  const ownershipFilterOptions = useMemo(() => {
    const set = new Set(OWNERSHIP_OPTIONS);
    profiles.forEach((p) => p.ownership_type && set.add(p.ownership_type));
    return [...set];
  }, [profiles]);

  const taxFilterOptions = useMemo(() => {
    const set = new Set(TAX_OPTIONS);
    profiles.forEach((p) => p.tax_system && set.add(p.tax_system));
    return [...set];
  }, [profiles]);

  const toggleExpanded = (id) => {
    const next = new Set(expanded);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    updateParams({ open: [...next].join(",") });
  };

  const openCreate = () => {
    setModalMode("create");
    setModalTarget(null);
    setForm(EMPTY_FORM);
    setInitialForm(EMPTY_FORM);
    setActionError("");
    setInnError("");
    setFeeError("");
    setDayError("");
    setSalaryDayError("");
    setAdvanceDayError("");
    setReminderFieldErrors([]);
  };

  const openEdit = useCallback((profile) => {
    const values = toFormValues(profile);
    setModalMode("edit");
    setModalTarget(profile);
    setForm(values);
    setInitialForm(values);
    setActionError("");
    setInnError("");
    setFeeError("");
    setDayError("");
    setSalaryDayError("");
    setAdvanceDayError("");
    setReminderFieldErrors([]);
  }, []);

  const clearCardParam = useCallback(() => {
    if (searchParams.get("card")) updateParams({ card: "" });
  }, [searchParams, updateParams]);

  const closeModal = () => {
    if (saving) return;
    if (formDirty && !window.confirm("Закрыть без сохранения изменений?")) return;
    setModalMode(null);
    setModalTarget(null);
    setInnError("");
    setFeeError("");
    setDayError("");
    setSalaryDayError("");
    setAdvanceDayError("");
    setReminderFieldErrors([]);
    clearCardParam();
  };

  useEffect(() => {
    if (loading || !requestedCardId) return;
    if (modalMode === "edit" && modalTarget?.id === requestedCardId) return;
    if (modalMode === "create") return;

    const found = profiles.find((profile) => profile.id === requestedCardId);
    if (found) {
      openEdit(found);
      return;
    }

    if (cardLookupRef.current === requestedCardId) return;
    let cancelled = false;
    fetchClientProfiles("all")
      .then((all) => {
        if (cancelled) return;
        cardLookupRef.current = requestedCardId;
        const match = Array.isArray(all) ? all.find((profile) => profile.id === requestedCardId) : null;
        if (match) openEdit(match);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [loading, requestedCardId, profiles, modalMode, modalTarget, openEdit]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const inn = form.inn.trim();
    if (inn && !/^\d{10}$|^\d{12}$/.test(inn)) {
      setInnError("ИНН должен содержать 10 или 12 цифр");
      setFeeError("");
      setDayError("");
      setSalaryDayError("");
      setAdvanceDayError("");
      setReminderFieldErrors([]);
      setActionError("");
      innRef.current?.focus();
      return;
    }
    if (canEditFee) {
      const fee = parseMonthlyFee(form.monthly_fee);
      if (!fee.ok) {
        setFeeError("Сумма должна быть числом не меньше 0");
        setDayError("");
        setSalaryDayError("");
        setAdvanceDayError("");
        setReminderFieldErrors([]);
        setInnError("");
        setActionError("");
        feeRef.current?.focus();
        return;
      }
      const day = parsePaymentDay(form.payment_day);
      if (!day.ok) {
        setDayError("День оплаты — число от 1 до 31");
        setFeeError("");
        setSalaryDayError("");
        setAdvanceDayError("");
        setReminderFieldErrors([]);
        setInnError("");
        setActionError("");
        dayRef.current?.focus();
        return;
      }
    }
    if (form.has_employees === "yes") {
      const salary = parsePaymentDay(form.salary_day);
      if (!salary.ok) {
        setSalaryDayError("День ЗП — число от 1 до 31");
        setAdvanceDayError("");
        setReminderFieldErrors([]);
        return;
      }
      const advance = parsePaymentDay(form.advance_day);
      if (!advance.ok) {
        setAdvanceDayError("День аванса — число от 1 до 31");
        setSalaryDayError("");
        setReminderFieldErrors([]);
        return;
      }
    }
    const nextReminderErrors = reminderErrors(form.reminders);
    if (nextReminderErrors.some(Boolean)) {
      setReminderFieldErrors(nextReminderErrors);
      return;
    }
    setSaving(true);
    setActionError("");
    setInnError("");
    setFeeError("");
    setDayError("");
    setSalaryDayError("");
    setAdvanceDayError("");
    setReminderFieldErrors([]);
    try {
      const payload = toPayload(form, { includeFee: canEditFee });
      if (modalMode === "edit" && modalTarget) {
        await updateClientProfile(modalTarget.id, payload);
      } else {
        await createClientProfile(payload);
      }
      setModalMode(null);
      setModalTarget(null);
      clearCardParam();
      await loadProfiles({ soft: true });
    } catch (err) {
      setActionError(getApiErrorMessage(err, "Не удалось сохранить клиента"));
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async () => {
    if (!archiveTarget) return;
    setArchiving(true);
    setActionError("");
    try {
      await archiveClientProfile(archiveTarget.id);
      setArchiveTarget(null);
      await loadProfiles({ soft: true });
    } catch (err) {
      setActionError(getApiErrorMessage(err, "Не удалось перенести клиента в архив"));
    } finally {
      setArchiving(false);
    }
  };

  const handleRestore = async (profile) => {
    setActionError("");
    setBusyId(profile.id);
    try {
      await updateClientProfile(profile.id, { is_active: true });
      await loadProfiles({ soft: true });
    } catch (err) {
      setActionError(getApiErrorMessage(err, "Не удалось восстановить клиента"));
    } finally {
      setBusyId(null);
    }
  };

  const patchMonthItem = async (profile, code, state) => {
    const previousItemState = (profile.month_check?.items || []).find((item) => item.code === code)?.state;
    const key = monthCheckBusyKey(profile.id, code);
    setActionError("");
    setMonthCheckBusy((prev) => {
      const next = new Set(prev);
      next.add(key);
      return next;
    });
    setProfiles((list) => list.map((row) => {
      if (row.id !== profile.id || !row.month_check) return row;
      return {
        ...row,
        month_check: withMonthCheckItemState(row.month_check, code, state),
      };
    }));
    try {
      const monthCheck = await patchMonthCheck(profile.id, { code, state });
      setProfiles((list) => list.map((row) => {
        if (row.id !== profile.id || !row.month_check) return row;
        return {
          ...row,
          month_check: applyReturnedMonthCheckItem(row.month_check, monthCheck, code),
        };
      }));
    } catch (err) {
      setProfiles((list) => list.map((row) => {
        if (row.id !== profile.id || !row.month_check) return row;
        return {
          ...row,
          month_check: withMonthCheckItemState(row.month_check, code, previousItemState),
        };
      }));
      setActionError(getApiErrorMessage(err, "Не удалось сохранить отметку"));
    } finally {
      setMonthCheckBusy((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  const toggleSurvey = async (profile) => {
    setActionError("");
    setBusyId(profile.id);
    try {
      await updateClientProfile(profile.id, {
        is_survey_excluded: !profile.is_survey_excluded,
      });
      await loadProfiles({ soft: true });
    } catch (err) {
      setActionError(getApiErrorMessage(err, "Не удалось изменить участие в опросе"));
    } finally {
      setBusyId(null);
    }
  };

  const toggleKontur = async (profile) => {
    if (
      profile.uses_kontur
      && !window.confirm(`Убрать «${profile.name}» из учёта Контура? История оплат останется.`)
    ) {
      return;
    }
    setActionError("");
    setBusyId(profile.id);
    try {
      await updateClientProfile(profile.id, {
        uses_kontur: !profile.uses_kontur,
      });
      await loadProfiles({ soft: true });
    } catch (err) {
      setActionError(getApiErrorMessage(err, "Не удалось изменить флаг кабинета Контура"));
    } finally {
      setBusyId(null);
    }
  };

  const hasFilters = Boolean(
    query || filterOwnership || filterTax || filterEmployees || filterSurvey
  );

  const toggleStatFilter = (key, value) => {
    const current = key === "survey" ? filterSurvey : filterEmployees;
    updateParams({ [key]: current === value ? "" : value });
  };

  return (
    <div className="page-container cd-page">
      <div className="page-header cd-header">
        <div>
          <h2>Клиенты</h2>
          <p className="page-subtitle cd-subtitle">
            Справочник, назначения и участие в опросах
          </p>
        </div>
        <div className="cd-header-actions">
          <button type="button" className="cd-add-btn" onClick={openCreate}>
            <Icons.Plus />
            Добавить клиента
          </button>
        </div>
      </div>

      {error && <ErrorRetry message={error} onRetry={() => loadProfiles()} />}
      {actionError && !modalMode && !archiveTarget && (
        <div className="login-error" role="status" aria-live="polite">{actionError}</div>
      )}

      <div className="cd-toolbar">
        <label className="cd-search">
          <Icons.Search />
          <span className="cd-visually-hidden">Поиск клиентов</span>
          <input
            type="search"
            name="q"
            autoComplete="off"
            aria-label="Поиск по названию, ИНН, СНО, собственности, информации"
            placeholder="Название, ИНН, СНО, собственность…"
            value={query}
            onChange={(e) => updateParams({ q: e.target.value })}
          />
        </label>
      </div>

      <div className="cd-filters">
        <label className="cd-filter">
          <span>Статус</span>
          <select
            className="cd-filter-select"
            name="status"
            autoComplete="off"
            value={statusFilter}
            onChange={(e) => updateParams({ status: e.target.value === "active" ? "" : e.target.value })}
            aria-label="Фильтр по статусу"
          >
            <option value="active">Активные</option>
            <option value="archived">Архив</option>
            <option value="all">Все</option>
          </select>
        </label>
        {!loading && profiles.length > 0 && (
          <>
          <label className="cd-filter">
            <span>Форма</span>
            <select
              className="cd-filter-select"
              name="ownership"
              autoComplete="off"
              value={filterOwnership}
              onChange={(e) => updateParams({ ownership: e.target.value })}
              aria-label="Фильтр по виду собственности"
            >
              <option value="">Все формы</option>
              {ownershipFilterOptions.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </label>
          <label className="cd-filter">
            <span>СНО</span>
            <select
              className="cd-filter-select"
              name="tax"
              autoComplete="off"
              value={filterTax}
              onChange={(e) => updateParams({ tax: e.target.value })}
              aria-label="Фильтр по системе налогообложения"
            >
              <option value="">Все СНО</option>
              {taxFilterOptions.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </label>
          <label className="cd-filter">
            <span>Сотрудники</span>
            <select
              className="cd-filter-select"
              name="employees"
              autoComplete="off"
              value={filterEmployees}
              onChange={(e) => updateParams({ employees: e.target.value })}
              aria-label="Фильтр по наличию сотрудников"
            >
              <option value="">Все</option>
              <option value="yes">С сотрудниками</option>
              <option value="no">Без сотрудников</option>
              <option value="unknown">Не указано</option>
            </select>
          </label>
          <label className="cd-filter">
            <span>Опрос</span>
            <select
              className="cd-filter-select"
              name="survey"
              autoComplete="off"
              value={filterSurvey}
              onChange={(e) => updateParams({ survey: e.target.value })}
              aria-label="Фильтр по участию в опросе"
            >
              <option value="">Все</option>
              <option value="in">В опросе</option>
              <option value="out">Вне опроса</option>
            </select>
          </label>
          <button
            type="button"
            className="cd-sort-btn"
            onClick={() => updateParams({ sort: sortDir === "asc" ? "desc" : "" })}
            aria-label={
              sortDir === "asc"
                ? "Сортировка: А–Я. Переключить на Я–А"
                : "Сортировка: Я–А. Переключить на А–Я"
            }
          >
            {sortDir === "asc" ? "А–Я" : "Я–А"}
          </button>
          {hasFilters && (
            <button
              type="button"
              className="cd-filter-reset"
              onClick={() => updateParams({
                q: "",
                ownership: "",
                tax: "",
                employees: "",
                survey: "",
              })}
            >
              Сбросить
            </button>
          )}
          </>
        )}
      </div>

      {!loading && profiles.length > 0 && (
        <section className="cd-metrics" aria-label="Сводка по клиентам">
          <div className="cd-metric">
            <span>Всего</span>
            <strong>{profiles.length}</strong>
          </div>
          <button
            type="button"
            className={`cd-metric ${filterSurvey === "in" ? "cd-metric--active" : ""}`}
            aria-pressed={filterSurvey === "in"}
            onClick={() => toggleStatFilter("survey", "in")}
          >
            <span>В опросе</span>
            <strong>{stats.inSurvey}</strong>
          </button>
          <button
            type="button"
            className={`cd-metric ${filterEmployees === "yes" ? "cd-metric--active" : ""}`}
            aria-pressed={filterEmployees === "yes"}
            onClick={() => toggleStatFilter("employees", "yes")}
          >
            <span>С сотрудниками</span>
            <strong>{stats.withEmployees}</strong>
          </button>
          <button
            type="button"
            className={`cd-metric ${filterEmployees === "unknown" ? "cd-metric--active" : ""}`}
            aria-pressed={filterEmployees === "unknown"}
            onClick={() => toggleStatFilter("employees", "unknown")}
          >
            <span>Не указано</span>
            <strong>{stats.unknownEmployees}</strong>
          </button>
          {hasFilters && (
            <div className="cd-metric">
              <span>Найдено</span>
              <strong>{filtered.length}</strong>
            </div>
          )}
        </section>
      )}

      {loading ? (
        <Loading text="Загрузка клиентов…" />
      ) : profiles.length === 0 ? (
        <div className="cd-empty">
          <Icons.Empty />
          <p>
            {statusFilter === "archived"
              ? "В архиве пока нет клиентов."
              : "Пока нет клиентов. Добавьте первого по кнопке выше."}
          </p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="cd-empty">
          <Icons.Empty />
          <p>Ничего не найдено{query ? ` по запросу «${query}»` : " по выбранным фильтрам"}.</p>
        </div>
      ) : (
        <div className="cd-list" role="list">
          {filtered.map((profile) => (
            <div key={profile.id} id={`client-row-${profile.id}`} role="listitem">
              <ClientRow
                profile={profile}
                expanded={expanded.has(profile.id)}
                onToggle={toggleExpanded}
                onEdit={openEdit}
                onArchive={(p) => {
                  setArchiveTarget(p);
                  setActionError("");
                }}
                onRestore={handleRestore}
                onToggleSurvey={toggleSurvey}
                onToggleKontur={toggleKontur}
                surveyBusy={busyId === profile.id}
                restoreBusy={busyId === profile.id}
                showFee={canEditFee}
                onMonthCheck={patchMonthItem}
                monthCheckBusy={monthCheckBusy}
              />
            </div>
          ))}
        </div>
      )}

      {modalMode && (
        <div className="users-modal-backdrop cd-modal-backdrop" onClick={closeModal}>
          <div
            className="users-modal client-edit-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="client-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="client-modal-title">{modalMode === "edit" ? "Редактирование клиента" : "Новый клиент"}</h3>
            {actionError && <div className="login-error" role="status" aria-live="polite">{actionError}</div>}
            <form className="users-form-grid" onSubmit={handleSubmit}>
              <ClientForm
                form={form}
                setForm={setForm}
                disabled={saving}
                assignees={assignees}
                innError={innError}
                innRef={innRef}
                nameRef={nameRef}
                feeError={feeError}
                feeRef={feeRef}
                dayError={dayError}
                dayRef={dayRef}
                salaryDayError={salaryDayError}
                advanceDayError={advanceDayError}
                reminderErrors={reminderFieldErrors}
                showFee={canEditFee}
              />
              <div className="users-modal-actions client-form-info">
                <button type="button" className="users-action-btn" onClick={closeModal} disabled={saving}>
                  Отмена
                </button>
                <button type="submit" className="login-button" disabled={saving}>
                  {saving ? "Сохранение…" : modalMode === "edit" ? "Сохранить" : "Добавить"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {archiveTarget && (
        <div className="users-modal-backdrop cd-modal-backdrop" onClick={() => !archiving && setArchiveTarget(null)}>
          <div
            className="users-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="client-archive-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="client-archive-title">В архив?</h3>
            <p className="settings-hint">
              Клиент «{archiveTarget.name}» будет скрыт из активных списков и опросов.
              Данные и история сохранятся — позже можно восстановить.
            </p>
            {actionError && (
              <div className="login-error cd-archive-error" role="status" aria-live="polite">
                {actionError}
              </div>
            )}
            <div className="users-modal-actions">
              <button type="button" className="users-action-btn" onClick={() => setArchiveTarget(null)} disabled={archiving}>
                Отмена
              </button>
              <button type="button" className="cd-danger-btn" onClick={handleArchive} disabled={archiving}>
                {archiving ? "Перенос…" : "В архив"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
